import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

/**
 * Upload one or more files as attachments on a card. Uploads sequentially,
 * invalidating the card + board queries after each so the list updates
 * progressively. Shared by the "+ Add" button and the modal drop zone.
 */
export function useAttachmentUpload(boardId: string, cardId: string) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["card", cardId] });
    qc.invalidateQueries({ queryKey: ["board", boardId] });
  };

  async function uploadFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    setError(null);
    setPending((n) => n + list.length);
    for (const file of list) {
      try {
        await api.attachments.upload(cardId, file);
        invalidate();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setPending((n) => n - 1);
      }
    }
  }

  return {
    uploadFiles,
    isUploading: pending > 0,
    error,
    clearError: () => setError(null),
  };
}

export type AttachmentUploader = ReturnType<typeof useAttachmentUpload>;
